import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search, UserPlus, X, ShieldCheck, Ban, UserCog, Mail, AlertTriangle, Copy, ExternalLink } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose
} from "@/components/ui/dialog";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { callAdminOperation } from '@/utils/stripeUtils';

type UserRole = 'admin' | 'user' | 'nutrition_expert';

interface User {
  id: string;
  email: string;
  role: UserRole;
  name?: string;
  createdAt?: string;
  lastLogin?: string;
  status?: 'active' | 'banned';
  subscription?: 'active' | 'inactive' | 'trial';
  isSpecialAdmin?: boolean;
}

const AdminUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'user' as UserRole,
  });
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showErrorDetailsDialog, setShowErrorDetailsDialog] = useState(false);
  const [errorDetails, setErrorDetails] = useState<any>(null);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Check if user is admin
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('No tienes permisos para acceder a esta página');
      navigate('/');
    }
  }, [user, navigate]);

  // Fetch users from Supabase
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log("Fetching users from admin-operations function...");
        
        // Call the admin-operations function to list users
        const data = await callAdminOperation('listUsers');
        
        console.log("Users data received:", data);
        
        if (data && data.success && data.data) {
          // Transform the user data to match our interface
          const transformedUsers: User[] = data.data.map(u => ({
            id: u.id,
            email: u.email || '',
            role: u.role || 'user',
            name: u.name || u.email.split('@')[0],
            createdAt: u.created_at ? new Date(u.created_at).toISOString().split('T')[0] : undefined,
            lastLogin: u.last_sign_in_at ? new Date(u.last_sign_in_at).toISOString().split('T')[0] : undefined,
            status: u.is_banned ? 'banned' : 'active',
            subscription: 'inactive', // Default value, update with actual subscription data if available
            isSpecialAdmin: u.is_special_admin
          }));
          
          setUsers(transformedUsers);
        } else {
          console.warn("Unexpected response format:", data);
          setError('Formato de respuesta inesperado al cargar usuarios');
          toast.error('Formato de respuesta inesperado al cargar usuarios');
        }
      } catch (error) {
        console.error("Error in fetchUsers:", error);
        setError(`Error al cargar usuarios: ${error instanceof Error ? error.message : String(error)}`);
        toast.error(`Error al cargar usuarios: ${error instanceof Error ? error.message : String(error)}`);
      } finally {
        setLoading(false);
      }
    };

    if (user && user.role === 'admin') {
      fetchUsers();
    }
  }, [user]);

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    try {
      // Update the role in the database
      await callAdminOperation('updateUserRole', { userId, role: newRole });
      
      // Update the UI
      const updatedUsers = users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ) as User[];
      
      setUsers(updatedUsers);
      toast.success('Rol de usuario actualizado');
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error(`Error al actualizar rol: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    // Find the user to check if it's a special admin
    const userToDelete = users.find(u => u.id === userId);
    
    if (userToDelete?.isSpecialAdmin) {
      toast.error('No se pueden eliminar usuarios administradores especiales');
      return;
    }

    if (window.confirm('¿Estás seguro de que quieres eliminar este usuario? Esta acción no se puede deshacer.')) {
      try {
        setIsDeleting(userId);
        console.log(`Attempting to delete user: ${userId}`);
        
        // Call the admin-operations function to delete the user
        const data = await callAdminOperation('deleteUser', { userId });
        
        console.log("Delete response:", data);
        
        // Check the response
        if (data && data.success) {
          // Update the UI by removing the user
          const updatedUsers = users.filter(user => user.id !== userId);
          setUsers(updatedUsers);
          toast.success('Usuario eliminado correctamente');
        } else {
          console.error("Delete operation failed:", data);
          const errorMessage = data?.error || 'No se pudo eliminar el usuario';
          toast.error(`Error al eliminar usuario: ${errorMessage}`);
        }
      } catch (error) {
        console.error("Error deleting user:", error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        toast.error(`Error al eliminar usuario: ${errorMessage}`);
      } finally {
        setIsDeleting(null);
      }
    }
  };

  const handleToggleStatus = async (userId: string) => {
    try {
      // Find the current user status
      const userToUpdate = users.find(u => u.id === userId);
      if (!userToUpdate) return;
      
      const newStatus = userToUpdate.status === 'active' ? 'banned' : 'active';
      
      // Call the admin-operations function to toggle the ban status
      const data = await callAdminOperation('toggleUserBan', { userId });
      
      // Update the UI
      const updatedUsers = users.map(user => 
        user.id === userId ? { ...user, status: newStatus } : user
      ) as User[];
      
      setUsers(updatedUsers);
      toast.success(`Usuario ${newStatus === 'active' ? 'activado' : 'desactivado'} correctamente`);
    } catch (error) {
      console.error("Error updating user status:", error);
      toast.error(`Error al actualizar estado: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.name) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    try {
      // Set loading state
      setIsAddingUser(true);
      
      // Show loading toast to indicate operation in progress
      const loadingToast = toast.loading('Creando usuario...');
      
      console.log("Calling createUser admin operation with:", { 
        email: newUser.email,
        name: newUser.name,
        role: newUser.role
      });
      
      // Call the admin-operations function to create a user
      const data = await callAdminOperation('createUser', { 
        email: newUser.email,
        name: newUser.name,
        role: newUser.role
      });

      console.log("Create user response:", data);
      
      // Remove loading toast
      toast.dismiss(loadingToast);
      
      if (data && data.success && data.user) {
        const addedUser: User = {
          id: data.user.id,
          email: data.user.email,
          name: data.user.name || newUser.name,
          role: data.user.role || newUser.role,
          createdAt: new Date().toISOString().split('T')[0],
          lastLogin: '-',
          status: 'active',
          subscription: 'trial',
        };

        setUsers([...users, addedUser]);
        
        // Check if resetLink was returned
        if (data.resetLink) {
          setResetLink(data.resetLink);
          setShowResetDialog(true);
        } else {
          setAddUserOpen(false);
          toast.success('Usuario añadido correctamente');
        }
        
        setNewUser({ name: '', email: '', role: 'user' });
      } else {
        if (data && data.error) {
          console.error("Error response:", data);
          toast.error(data.error);
        } else {
          console.error("Unexpected response:", data);
          throw new Error('No se pudo crear el usuario');
        }
      }
    } catch (error: any) {
      console.error("Error creating user:", error);
      
      // Check specifically for "email already exists" error which comes from the API
      if (error.message?.toLowerCase().includes('email address has already been registered') || 
          error.message?.toLowerCase().includes('email_exists') ||
          error.message?.toLowerCase().includes('ya existe') ||
          error.message?.toLowerCase().includes('ya está registrado')) {
        toast.error(`Este email ya está registrado. Por favor utiliza un email diferente.`);
      } else {
        toast.error(`Error al crear usuario: ${error instanceof Error ? error.message : String(error)}`);
      }
    } finally {
      setIsAddingUser(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Enlace copiado al portapapeles');
    }).catch((err) => {
      console.error('Error al copiar:', err);
      toast.error('Error al copiar el enlace');
    });
  };

  const handleSendNotification = (email: string) => {
    toast.success(`Notificación enviada a ${email}`);
  };

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusBadge = (status: string | undefined, isSpecialAdmin: boolean | undefined) => {
    if (isSpecialAdmin) {
      return <Badge className="bg-purple-500">Admin Especial</Badge>;
    }
    
    switch(status) {
      case 'active':
        return <Badge className="bg-green-500">Activo</Badge>;
      case 'banned':
        return <Badge className="bg-red-500">Bloqueado</Badge>;
      default:
        return <Badge className="bg-gray-500">Desconocido</Badge>;
    }
  };

  const getSubscriptionBadge = (subscription: string | undefined) => {
    switch(subscription) {
      case 'active':
        return <Badge className="bg-blue-500">Premium</Badge>;
      case 'trial':
        return <Badge className="bg-orange-500">Prueba</Badge>;
      case 'inactive':
        return <Badge className="bg-gray-400">Básico</Badge>;
      default:
        return <Badge className="bg-gray-500">Desconocido</Badge>;
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <div className="flex-grow py-8">
        <div className="container mx-auto px-4">
          <Card>
            <CardHeader className="bg-vegana-secondary/10 rounded-t-lg">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <CardTitle className="text-2xl text-vegana-primary">Gestión de Usuarios</CardTitle>
                  <CardDescription>
                    Administra los usuarios y sus roles en la plataforma
                  </CardDescription>
                </div>
                <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-vegana-primary">
                      <UserPlus className="mr-2 h-4 w-4" />
                      Añadir Usuario
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Añadir Nuevo Usuario</DialogTitle>
                      <DialogDescription>
                        Completa el formulario para crear un nuevo usuario en el sistema.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="name" className="col-span-1">
                          Nombre
                        </Label>
                        <Input
                          id="name"
                          placeholder="Nombre completo"
                          value={newUser.name}
                          onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                          className="col-span-3"
                        />
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="email" className="col-span-1">
                          Email
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="email@ejemplo.com"
                          value={newUser.email}
                          onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                          className="col-span-3"
                        />
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="role" className="col-span-1">
                          Rol
                        </Label>
                        <Select
                          value={newUser.role}
                          onValueChange={(value: any) => setNewUser({...newUser, role: value})}
                        >
                          <SelectTrigger className="col-span-3">
                            <SelectValue placeholder="Selecciona un rol" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Administrador</SelectItem>
                            <SelectItem value="nutrition_expert">Nutricionista</SelectItem>
                            <SelectItem value="user">Usuario</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAddUserOpen(false)}>
                        Cancelar
                      </Button>
                      <Button 
                        className="bg-vegana-primary" 
                        onClick={handleAddUser} 
                        disabled={isAddingUser || !newUser.email || !newUser.name}
                      >
                        {isAddingUser ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Guardando...
                          </>
                        ) : (
                          'Guardar Usuario'
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Usuario creado correctamente</DialogTitle>
                    <DialogDescription>
                      Comparte este enlace con el usuario para que pueda establecer su contraseña.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="my-4">
                    <Alert className="mb-4">
                      <AlertTitle className="text-amber-500 flex items-center gap-2">
                        <AlertTriangle size={16} />
                        Importante
                      </AlertTitle>
                      <AlertDescription>
                        Este enlace solo se mostrará una vez. Asegúrate de copiarlo o enviarlo al usuario ahora.
                      </AlertDescription>
                    </Alert>
                    <div className="bg-gray-100 p-3 rounded-md relative text-sm break-all">
                      {resetLink}
                      <div className="absolute right-2 top-2 flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6" 
                          onClick={() => copyToClipboard(resetLink || '')}
                        >
                          <Copy size={14} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6" 
                          onClick={() => window.open(resetLink || '', '_blank')}
                        >
                          <ExternalLink size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button 
                        className="bg-vegana-primary"
                        onClick={() => {
                          setShowResetDialog(false);
                          setAddUserOpen(false);
                          toast.success('Usuario añadido correctamente');
                        }}
                      >
                        Entendido
                      </Button>
                    </DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Error Details Dialog */}
              <Dialog open={showErrorDetailsDialog} onOpenChange={setShowErrorDetailsDialog}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-600">
                      <AlertTriangle size={20} />
                      Error al eliminar usuario
                    </DialogTitle>
                    <DialogDescription>
                      No se pudo eliminar el usuario porque tiene datos asociados.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="my-4">
                    <Alert className="mb-4 border-red-200 bg-red-50">
                      <AlertTitle className="text-red-600">
                        Problema
                      </AlertTitle>
                      <AlertDescription className="text-red-600">
                        {errorDetails?.error || 'Error desconocido'}
                      </AlertDescription>
                    </Alert>
                    {errorDetails?.details && errorDetails.details.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-medium mb-2">Detalles técnicos:</h4>
                        <div className="bg-gray-100 p-3 rounded-md text-sm max-h-60 overflow-y-auto">
                          <pre>{JSON.stringify(errorDetails.details, null, 2)}</pre>
                        </div>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button 
                        className="bg-vegana-primary"
                        onClick={() => {
                          setShowErrorDetailsDialog(false);
                          setErrorDetails(null);
                        }}
                      >
                        Entendido
                      </Button>
                    </DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <div className="flex flex-col md:flex-row justify-between mb-6 gap-4">
                <div className="relative w-full md:w-64">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar usuarios..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                  {searchTerm && (
                    <button 
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2 top-2.5"
                    >
                      <X className="h-4 w-4 text-muted-foreground" />
                    </button>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  Total: {filteredUsers.length} usuario(s)
                </div>
              </div>

              {error && (
                <div className="mb-6 p-4 border border-red-200 bg-red-50 rounded-md flex items-center gap-2 text-red-700">
                  <AlertTriangle size={16} />
                  <p>{error}</p>
                </div>
              )}

              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-vegana-primary" />
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Usuario</TableHead>
                        <TableHead>Rol</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Suscripción</TableHead>
                        <TableHead>Fecha de registro</TableHead>
                        <TableHead>Último acceso</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{user.name || 'Sin nombre'}</p>
                                <p className="text-sm text-gray-500">{user.email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Select
                                defaultValue={user.role}
                                onValueChange={(value: UserRole) => handleRoleChange(user.id, value)}
                                disabled={user.isSpecialAdmin}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="admin">Admin</SelectItem>
                                  <SelectItem value="nutrition_expert">Nutricionista</SelectItem>
                                  <SelectItem value="user">Usuario</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>{getStatusBadge(user.status, user.isSpecialAdmin)}</TableCell>
                            <TableCell>{getSubscriptionBadge(user.subscription)}</TableCell>
                            <TableCell>{user.createdAt || 'N/A'}</TableCell>
                            <TableCell>{user.lastLogin || 'N/A'}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleToggleStatus(user.id)}
                                  className={`${user.status === 'banned' 
                                    ? 'text-green-600 hover:text-green-700 hover:bg-green-50' 
                                    : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'}`}
                                  disabled={user.isSpecialAdmin}
                                >
                                  {user.status === 'banned' ? <ShieldCheck size={16} /> : <Ban size={16} />}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleSendNotification(user.email)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Mail size={16} />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteUser(user.id)}
                                  disabled={isDeleting === user.id || user.isSpecialAdmin}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  {isDeleting === user.id ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                            No se encontraron usuarios que coincidan con la búsqueda
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminUsers;
